import React, { useEffect, useState } from "react";
import { message } from "antd";
import CustomModal from "../Modal";
import { sendReportEmail, looksLikeEmail } from "../../../api/emailApi";
import "./emailReportModal.css";

/**
 * Email this report to someone, as a PDF.
 *
 * The attachment is built when Send is pressed, not when the dialog opens: the PDF for a
 * large report takes a second or two to render, and doing it on open would make the
 * button that opens the dialog feel broken. It also means the file reflects the filters
 * as they are at the moment of sending.
 *
 * `buildAttachments` is passed in rather than imported - the dialog does not know what it
 * is attaching, only that the caller can produce it. It returns a list of
 * { fileName, fileType, fileContent }: one entry for a report, one per table widget for a
 * dashboard, and an empty list when there is nothing to send.
 *
 * One recipient only: `To` and `ToName` are single strings in the email registry's
 * contract. See api/emailApi.js.
 */
export default function EmailReportModal({ open, reportName, defaultReplyTo, buildAttachments, onClose }) {
  const [form, setForm] = useState({ toName: "", to: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  /* Shown under the field rather than as a toast: it is a correction to something on
     screen, and a toast would be gone by the time they looked back at the input. */
  const [error, setError] = useState("");

  /* Fresh every time it opens, with the subject seeded from the report's name - the
     thing a sender would type anyway. */
  useEffect(() => {
    if (!open) return;
    setForm({
      toName: "",
      to: "",
      subject: reportName ? `${reportName} report` : "Report",
      message: "",
    });
    setError("");
    setSending(false);
  }, [open, reportName]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSend = async () => {
    if (sending) return;

    if (!looksLikeEmail(form.to)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!form.subject.trim()) {
      setError("Please enter a subject.");
      return;
    }

    setError("");
    setSending(true);

    try {
      /* Rendering a wide report blocks for a moment - jsPDF is synchronous once it
         starts - so the button says what it is doing rather than looking dead. On a
         dashboard this is one PDF per table widget, built in parallel. */
      const attachments = await buildAttachments();
      if (!attachments || !attachments.length) {
        setError("There is no data in this report to send.");
        return;
      }

      await sendReportEmail({
        to: form.to,
        toName: form.toName,
        subject: form.subject,
        message: form.message,
        attachments,
        replyTo: defaultReplyTo,
      });

      message.success(`Report sent to ${form.to.trim()}`);
      onClose?.();
    } catch (err) {
      /* sendReportEmail has already logged the technical detail and put a sentence worth
         showing on the error. */
      setError(err?.message || "The report could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={sending ? undefined : onClose}
      title="Email this report"
      size="medium"
      footer={
        <div className="erm-footer">
          <button type="button" className="erm-btn erm-btn-ghost" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button type="button" className="erm-btn erm-btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      }
    >
      <p className="erm-intro">The report is attached as a PDF, exactly as it appears on screen.</p>

      <div className="erm-field">
        <label className="erm-label" htmlFor="erm-to-name">
          Recipient name
        </label>
        <input
          id="erm-to-name"
          className="erm-input"
          type="text"
          placeholder="Who is this going to?"
          value={form.toName}
          onChange={(e) => set({ toName: e.target.value })}
          disabled={sending}
        />
      </div>

      <div className="erm-field">
        <label className="erm-label" htmlFor="erm-to">
          Email address
        </label>
        <input
          id="erm-to"
          className="erm-input"
          type="email"
          placeholder="name@company.com"
          value={form.to}
          onChange={(e) => set({ to: e.target.value })}
          disabled={sending}
          autoComplete="off"
        />
      </div>

      <div className="erm-field">
        <label className="erm-label" htmlFor="erm-subject">
          Subject
        </label>
        <input
          id="erm-subject"
          className="erm-input"
          type="text"
          value={form.subject}
          onChange={(e) => set({ subject: e.target.value })}
          disabled={sending}
        />
      </div>

      <div className="erm-field">
        <label className="erm-label" htmlFor="erm-message">
          Message <span className="erm-optional">(optional)</span>
        </label>
        <textarea
          id="erm-message"
          className="erm-input erm-textarea"
          rows={3}
          placeholder="Please find the attached report."
          value={form.message}
          onChange={(e) => set({ message: e.target.value })}
          disabled={sending}
        />
      </div>

      {error ? (
        <div className="erm-error" role="alert">
          {error}
        </div>
      ) : null}
    </CustomModal>
  );
}
